import posthog from 'posthog-js';

export type AnalyticsConsentState = 'granted' | 'denied' | 'prompt' | 'loading';

const CONSENT_KEY = 'tgp_analytics_consent_v1';
const SIGNUP_SENT_PREFIX = 'tgp_ga_signup_sent:';
const PUBLIC_SITE_URL = import.meta.env.VITE_PUBLIC_SITE_URL || 'https://thegameproject.net';
const measurementId = import.meta.env.VITE_PUBLIC_GA_MEASUREMENT_ID?.trim();
const debugMode = import.meta.env.VITE_PUBLIC_GA_DEBUG === 'true';
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

let consentState: AnalyticsConsentState = 'loading';
let analyticsStarted = false;
let posthogStarted = false;
let initializationPromise: Promise<AnalyticsConsentState> | null = null;
const listeners = new Set<(state: AnalyticsConsentState) => void>();

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function publish(state: AnalyticsConsentState) {
  consentState = state;
  for (const listener of listeners) listener(state);
}

function cookieDomain(): string {
  return window.location.hostname === 'thegameproject.net' || window.location.hostname.endsWith('.thegameproject.net')
    ? '; Domain=.thegameproject.net'
    : '';
}

function readCookie(): 'granted' | 'denied' | null {
  const value = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${CONSENT_KEY}=`))
    ?.split('=')[1];
  return value === 'granted' || value === 'denied' ? value : null;
}

function writeCookie(value: 'granted' | 'denied') {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  // biome-ignore lint/suspicious/noDocumentCookie: a root-domain cookie shares consent between the marketing site and app.
  document.cookie = `${CONSENT_KEY}=${value}; Max-Age=31536000; Path=/; SameSite=Lax${secure}${cookieDomain()}`;
}

function migrateLegacyChoice(): 'granted' | 'denied' | null {
  const cookie = readCookie();
  if (cookie) return cookie;
  try {
    const legacy = window.localStorage.getItem(CONSENT_KEY);
    if (legacy === 'granted' || legacy === 'denied') {
      writeCookie(legacy);
      window.localStorage.removeItem(CONSENT_KEY);
      return legacy;
    }
  } catch {
    // Continue without persistent storage.
  }
  return null;
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function () {
      // biome-ignore lint/complexity/noArguments: GA4 requires Arguments objects to recognize gtag commands.
      window.dataLayer.push(arguments);
    };
}

function removeAnalyticsCookies() {
  const domains = [window.location.hostname, `.${window.location.hostname}`, '.thegameproject.net'];
  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0]?.trim();
    if (!name || (name !== '_ga' && !name.startsWith('_ga_') && !name.startsWith('ph_'))) continue;
    // biome-ignore lint/suspicious/noDocumentCookie: cookie removal is required when analytics consent is withdrawn.
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    for (const domain of domains) {
      // biome-ignore lint/suspicious/noDocumentCookie: cookie removal must cover host and shared production domains.
      document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${domain}; SameSite=Lax`;
    }
  }
}

function startAnalytics() {
  if (analyticsStarted) return;
  analyticsStarted = true;
  ensureGtag();
  window.gtag?.('consent', 'update', { analytics_storage: 'granted' });

  if (measurementId && /^G-[A-Z0-9]+$/.test(measurementId)) {
    window.gtag?.('js', new Date());
    window.gtag?.('config', measurementId, {
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      cookie_domain: 'auto',
      debug_mode: debugMode,
      send_page_view: false,
    });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.append(script);
  }

  if (posthogKey) {
    posthog.init(posthogKey, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
      loaded: (client) => {
        if (import.meta.env.DEV) client.opt_out_capturing();
      },
      defaults: '2025-11-30',
    });
    posthogStarted = true;
    if (!import.meta.env.DEV) posthog.opt_in_capturing();
  }
}

export function initializeAnalyticsConsent(): Promise<AnalyticsConsentState> {
  if (initializationPromise) return initializationPromise;
  initializationPromise = (async () => {
    ensureGtag();
    window.gtag?.('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500,
    });

    const explicitChoice = migrateLegacyChoice();
    if (explicitChoice === 'granted') {
      startAnalytics();
      publish('granted');
      return 'granted';
    }
    if (
      explicitChoice === 'denied' ||
      (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
    ) {
      publish('denied');
      return 'denied';
    }

    try {
      const response = await fetch(`${PUBLIC_SITE_URL}/api/analytics-consent`, {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Unable to resolve analytics consent region');
      const policy = (await response.json()) as { requiresConsent: boolean | null };
      if (policy.requiresConsent === false) {
        startAnalytics();
        publish('granted');
        return 'granted';
      }
    } catch {
      // Fail closed and ask for an explicit choice.
    }

    publish('prompt');
    return 'prompt';
  })();
  return initializationPromise;
}

export function subscribeToAnalyticsConsent(listener: (state: AnalyticsConsentState) => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getAnalyticsConsentState() {
  return consentState;
}

export function grantAnalyticsConsent() {
  writeCookie('granted');
  startAnalytics();
  publish('granted');
}

export function denyAnalyticsConsent() {
  const wasRunning = analyticsStarted;
  writeCookie('denied');
  ensureGtag();
  window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
  if (posthogStarted) {
    posthog.opt_out_capturing();
    posthog.reset();
  }
  removeAnalyticsCookies();
  publish('denied');
  if (wasRunning) window.location.reload();
}

export function openAnalyticsSettings() {
  publish('prompt');
}

export function setAnalyticsUserId(userId: string | null) {
  if (consentState !== 'granted') return;
  window.gtag?.('set', { user_id: userId });
}

export async function trackSignupStarted(): Promise<void> {
  await initializeAnalyticsConsent();
  if (consentState !== 'granted' || !window.gtag) return;
  return new Promise((resolve) => {
    let complete = false;
    const finish = () => {
      if (complete) return;
      complete = true;
      resolve();
    };
    window.gtag?.('event', 'signup_started', {
      method: 'auth0',
      event_callback: finish,
      event_timeout: 800,
    });
    window.setTimeout(finish, 850);
  });
}

export async function trackSignUp(userId: string) {
  await initializeAnalyticsConsent();
  if (consentState !== 'granted' || !window.gtag) return;
  const marker = `${SIGNUP_SENT_PREFIX}${userId}`;
  try {
    if (window.localStorage.getItem(marker) === 'true') return;
  } catch {
    // The backend creation flag remains the primary duplicate guard.
  }
  setAnalyticsUserId(userId);
  window.gtag('event', 'sign_up', { method: 'auth0' });
  try {
    window.localStorage.setItem(marker, 'true');
  } catch {
    // The backend creation flag remains the primary duplicate guard.
  }
}

export function resetAnalyticsUser() {
  setAnalyticsUserId(null);
  if (posthogStarted) posthog.reset();
}
